import { BoostData, BoostsData, BoostsJson, BoostsState, GearBonus, OfferScreen, activeBoosts, buyBoost, purchasesAllowed, withBoosts } from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import type { LevelCatalog } from "./levelCatalog";
import { NOT_SAVING_HERE } from "./purchases";

/**
 * Battle boosts (owner 2026-10-21, docs/SHOP.md §23) as use cases: what runs now and how long it has left, buying one
 * for gems (hub only), and the GearBonus list a battle started now takes (`gearFor`). Saved in the `boosts` section.
 */

export const boostsSection = registerSaveSection<BoostsJson>("boosts", (v) => (typeof v === "object" && v !== null && !Array.isArray(v) ? (v as BoostsJson) : undefined), (v) => v);

export function boostsFromSave(save: HasSections, catalog: LevelCatalog): BoostsState { return BoostsState.fromJson(catalog.balance, boostsSection.read(save)); }
export function boostsToSave(save: HasSections, state: BoostsState): void { boostsSection.write(save, state.toJson()); }

/** One boost as the Shop and the battle card show it: its data and the seconds it has left (0: not running). */
export interface BoostView { readonly boost: BoostData; readonly left: number }

export class Boosts {
  constructor(private readonly catalog: LevelCatalog) {}

  get data(): BoostsData { return this.catalog.balance.commerce.shop.boosts; }
  private get state(): BoostsState { return this.catalog.boostState; }

  /** The Shop sells them once `unlockAfterLevel` is cleared. */
  get shopOpen(): boolean { return this.data.items.length > 0 && this.catalog.highestLevelCleared >= this.data.unlockAfterLevel; }

  view(now: number): BoostView[] { return this.data.items.map((boost) => ({ boost, left: this.state.left(boost.id, now) })); }
  active(now: number): BoostData[] { return activeBoosts(this.catalog.balance, this.state, now); }

  /** Buys one for gems (hub only, never while the save cannot be written); the caller persists after a success. */
  buy(id: string, now: number, screen: OfferScreen = "hub"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    return buyBoost(this.catalog.balance, this.state, this.catalog.economy.wallet, id, now);
  }

  /** `gear` with the boosts running at `now` added: what a battle started now runs with. */
  gearFor(gear: readonly GearBonus[], now: number): GearBonus[] { return withBoosts(this.catalog.balance, gear, this.active(now)); }
}
